import React, { forwardRef, useCallback, useRef, useEffect } from 'react';
import * as echarts from 'echarts/lib/echarts';
import { useResizeObserver } from './hooks';
import { BaseChartProps } from './BaseChart.typed';
// eslint-disable-next-line react/display-name
const BaseChart = forwardRef((props: BaseChartProps, ref) => {
  const refHTML = useRef<HTMLDivElement>(null);
  const divRef = (ref as React.MutableRefObject<HTMLDivElement>) || refHTML;

  // init echarts
  const initEChart = useCallback(() => {
    if (!divRef.current) {
      return;
    }
    let echartInstance = echarts.getInstanceByDom(divRef.current);
    if (!echartInstance) {
      echartInstance = echarts.init(divRef.current, undefined, {
        renderer: props.renderer || 'canvas'
      });
    }

    if (Array.isArray(props.events) && props.events.length > 0) {
      props.events.forEach(([eventName]) => {
        echartInstance.off(eventName);
      });
    }

    echartInstance.clear();
    echartInstance.setOption(props.option);
  }, [divRef]);

  // destroy echarts
  const dispose = useCallback(() => {
    if (!divRef.current) {
      return;
    }
    const echartInstance = echarts.getInstanceByDom(divRef.current);
    if (!echartInstance) {
      return;
    }
    if (Array.isArray(props.events) && props.events.length > 0) {
      props.events.forEach(([eventName]) => {
        echartInstance.off(eventName);
      });
    }

    echartInstance.dispose();
  }, [divRef]);

  // did mount
  useEffect(() => {
    initEChart();
    return () => {
      dispose();
    }
  }, []);

  useEffect(() => {
    const echartInstance = echarts.getInstanceByDom(divRef.current);
    if (props.legends && divRef.current) {
      echartInstance.dispatchAction({
        type: 'legendAllSelect'
      })
      for (const legend of Object.keys(props.legends)) {
        if (!props.legends[legend]) {
          echartInstance.dispatchAction({
            type: 'legendUnSelect',
            name: legend,
          })
        }
      }
    }
  }, [props.legends, divRef.current])

  // events update
  useEffect(() => {
    if (!divRef.current) {
      return;
    }
    const echartInstance = echarts.getInstanceByDom(divRef.current);
    if (echartInstance && Array.isArray(props.events) && props.events.length > 0) {
      props.events.forEach(([eventName]) => {
        echartInstance.off(eventName);
      });

      props.events.forEach((event) => {
        echartInstance.on(...event);
      });
    }
  }, [props.events]);

  // option update
  useEffect(() => {
    if (!divRef.current) {
      return;
    }
    const echartInstance = echarts.getInstanceByDom(divRef.current);
    if (!echartInstance) {
      return;
    }

    echartInstance.setOption(props.option);
    
  }, [props.option]);

  useResizeObserver(divRef);

  return (
    <div className={props.className} ref={divRef} />
  );
});

export default BaseChart;
